import { axiosRequest } from '@/lib/axios/axios'

export const capitalize = (str: string) => {
	if (!str) return str
	return str.charAt(0).toUpperCase() + str.slice(1)
}

export const setPageCount = (p: number, c: number) => {
	return Math.ceil(c / p)
}

/**
 * 날짜 문자열을 'Jul 11'과 같은 en-US 형식의 월, 일로 변환합니다. 영어로만 반환됨.
 * @param date ISO 형식의 날짜 문자열 (예: '2025-07-11T13:02:12.593Z')
 * @returns 변환된 날짜 문자열 (예: 'Jul 11')
 */
export const formatDate = (date: string) => {
	return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * 날짜 문자열을 "YYYY.MM.DD HH:mm:SS" 형식으로 변환합니다.
 * @param dateString ISO 형식의 날짜 문자열 또는 Date 객체 (예: '2025-07-11T13:02:12.593Z')
 * @returns 변환된 날짜 문자열 (예: '2025.07.11 13:02:12')
 */
export const formatDateTime = (dateString: string | Date | null | undefined): string => {
	if (!dateString) return ''
	try {
		const date = dateString instanceof Date ? dateString : new Date(dateString)
		if (isNaN(date.getTime())) return String(dateString) // 유효하지 않은 날짜면 원본 반환
		const year = date.getFullYear()
		const month = String(date.getMonth() + 1).padStart(2, '0')
		const day = String(date.getDate()).padStart(2, '0')
		const hours = String(date.getHours()).padStart(2, '0')
		const minutes = String(date.getMinutes()).padStart(2, '0')
		const seconds = String(date.getSeconds()).padStart(2, '0')

		return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`
	} catch {
		return String(dateString)
	}
}

/**
 * 사이드 메뉴에서 사용할 각 routeName에 맞는 API 리스트를 조회합니다.
 * @param routeName 라우트 이름 (예: 'Project', 'Template' 등)
 * @returns 해당 routeName에 맞는 API 응답 데이터
 */
export const retrieveAsideBoardList = async (routeName: string) => {
	const urlMap: Record<string, string> = {
		Project: '/v1/project/retrieveList',
		Template: '/v1/template/retrieveList',
		Schema: '/v1/schema/retrieveList',
		Dropbox: '/v1/dropbox/retrieveList',
		Asset: '/v1/schema/retrieveList',
		Admin: '/v1/admin/user/retrieveList',
	}

	const url = urlMap[routeName] || ''

	return await axiosRequest<any>(url, {})
}

export function getRandomHexColor() {
	// '#' 뒤에 6자리의 랜덤 색상 코드 생성
	const letters = '0123456789ABCDEF'
	let color = '#'
	for (let i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)]
	}
	return color
}

/**
 * HTML 태그를 안전하게 문자열로 변환
 * @param text 변환할 텍스트
 * @returns 안전한 문자열
 */
export const escapeHtml = (text: string): string => {
	if (!text) return ''

	const div = document.createElement('div')
	div.textContent = text
	return div.innerHTML
}

export const unEscapeHtml = (input: string): string => {
	if (!input) return ''

	// 문자열에서 특정 문자를 이스케이프 처리
	const escapeMap: any = {
		'&lt;': '<',
		'&gt;': '>',
		'&quot;': '"',
		'&#39;': "'",
		'&amp;': '&', // 매우 중요: &를 먼저 처리해야 올바르게 작동
	}

	// 해당 문자들을 escapeMap을 통해 변환
	return input.replace(/&lt;|&gt;|&quot;|&#39;|&amp;/g, match => escapeMap[match])
}
/**
 * HTML 태그를 제거하고 순수 텍스트만 추출
 * @param html HTML 문자열
 * @returns 순수 텍스트
 */
export const stripHtml = (html: string): string => {
	if (!html) return ''

	const div = document.createElement('div')
	div.innerHTML = html
	return div.textContent || div.innerText || ''
}

const DANGEROUS_TAGS = [
	'script', 'iframe', 'object', 'embed', 'style', 'link',
	'form', 'input', 'textarea', 'select', 'button',
	'meta', 'base', 'applet', 'noscript', 'template',
	'slot', 'dialog', 'svg', 'math',
]

/**
 * HTML 콘텐츠에서 위험한 태그/속성을 무력화하여 XSS 인젝션을 방어
 * <script>, <iframe> 등 위험 요소를 실행 불가능한 텍스트로 변환하고
 * onclick, javascript: 등 이벤트 핸들러/위험 URL을 제거
 */
export const sanitizeCellContent = (html: string): string => {
	if (!html) return ''

	const tempDiv = document.createElement('div')
	tempDiv.innerHTML = html

	DANGEROUS_TAGS.forEach(tag => {
		tempDiv.querySelectorAll(tag).forEach(el => {
			const textNode = document.createTextNode(el.outerHTML)
			el.parentNode?.replaceChild(textNode, el)
		})
	})

	tempDiv.querySelectorAll('*').forEach(el => {
		const attrs = [...el.attributes]
		for (const attr of attrs) {
			const name = attr.name.toLowerCase()
			if (name.startsWith('on')) {
				el.removeAttribute(attr.name)
				continue
			}
			const value = attr.value.toLowerCase().replace(/\s/g, '')
			if (
				(name === 'href' || name === 'src' || name === 'action' ||
				 name === 'formaction' || name === 'xlink:href' ||
				 name === 'background' || name === 'dynsrc' || name === 'lowsrc') &&
				(value.startsWith('javascript:') || value.startsWith('vbscript:') || value.startsWith('data:text/html'))
			) {
				el.removeAttribute(attr.name)
			}
		}
	})

	return tempDiv.innerHTML
}

export const stripFunctionCloseButton = (html: string): string => {
	if (!html) return ''
	if (!html.includes('ico--table__close')) return html

	const tempDiv = document.createElement('div')
	tempDiv.innerHTML = html
	tempDiv.querySelectorAll('.ico--table__close').forEach(el => el.remove())
	return tempDiv.innerHTML
}

export const normalizeWonSign = (text: string): string => {
	// Windows/한국어 키보드 환경에서 원화 기호(₩)가 백슬래시(\)로 입력되는 케이스를 정규화
	// 예: "\"(U+005C) -> "₩"(U+20A9)
	return (text || '').replace(/\\/g, '₩')
}
